/**
 * CSV export for a v2 report: a short preamble (report, date range, filters,
 * grouping), a blank line, then the sorted tree flattened depth-first, one
 * row per node (date buckets, then each nested group), with a Level column so
 * a spreadsheet can filter to one level, and a final Total row from the API.
 *
 * Values are raw numbers (money to 2 dp, percents on a 0–100 scale, blanks
 * for unavailable values) rather than display strings, and nothing is summed
 * here: every subtotal and the Total row come from the API, so distinct
 * counts and percentages are never added up.
 */
import { downloadCSV, toCSV } from "../../../../utils/csvUtils";
import {
  ReportMetricDef,
  ReportTreeNodeV2,
  RunReportV2Response,
} from "../../../../types/reportsV2";
import { ReportTableModel } from "../hooks/useReportTable";
import { MetricIndex, valueOf } from "../model/reportTree";
import { RunContext } from "../model/runContext";

export interface ReportCsvInput {
  /** Report display name (used for the file name). */
  name: string;
  dateRange: { start: string; end: string };
  /** Visible metric columns, in view order. */
  columns: ReportMetricDef[];
  /** Tree sorted as on screen. */
  sortedNodes: ReportTreeNodeV2[];
  index: MetricIndex;
  totals: Array<number | null>;
  /** Grouping label per depth: [date level, group 1, group 2, …]. */
  levelLabels: string[];
  /** What the run covers, written as the preamble. */
  context: RunContext;
}

type Cell = string | number | null;

const LEVEL_HEADER = "Level";
const GROUPING_HEADER = "Grouping";
const NAME_HEADER = "Name";
const TOTAL_LABEL = "Total";

/** Spreadsheet apps run cells starting with = + - @ as formulas; keep text
 *  labels (staff / item names) inert. Numbers are left as numbers. */
export function inertText(value: string): string {
  return /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
}

/** Header + rows, ready for csvUtils.toCSV. */
export function buildReportCsv(input: ReportCsvInput): {
  header: string[];
  rows: Cell[][];
} {
  const { columns, index } = input;
  const metricCells = (values: Array<number | null>): Cell[] =>
    columns.map((m) => valueOf(values, index, m.key));
  const rows: Cell[][] = [];
  const walk = (nodes: ReportTreeNodeV2[], depth: number) => {
    nodes.forEach((node) => {
      rows.push([
        depth + 1,
        inertText(input.levelLabels[depth] ?? node.dimension),
        // Date buckets export their ISO start date (sortable in a sheet).
        depth === 0 ? node.key : inertText(node.label),
        ...metricCells(node.values),
      ]);
      walk(node.children ?? [], depth + 1);
    });
  };
  walk(input.sortedNodes, 0);
  rows.push(["", TOTAL_LABEL, "", ...metricCells(input.totals)]);
  return {
    header: [
      LEVEL_HEADER,
      GROUPING_HEADER,
      NAME_HEADER,
      // Payment-method and discount column labels are salon-entered names.
      ...columns.map((m) => inertText(m.label)),
    ],
    rows,
  };
}

/** "daily-sales-report_2026-10-01_2026-10-07.csv" */
export function reportCsvFileName(
  name: string,
  { start, end }: { start: string; end: string },
): string {
  const slug =
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "") || "report";
  return `${slug}_${start}_${end}.csv`;
}

/** "Report,<name>" / "Date range,…" / "Filters,…" / "Grouped by,…" lines. */
export function reportCsvPreamble(name: string, context: RunContext): string {
  const pairs: Array<[string, string]> = [
    ["Date range", context.range],
    ["Filters", context.filters],
    ["Grouped by", context.groupedBy],
  ];
  return toCSV(
    pairs.map(([key, value]) => [key, inertText(value)]),
    [
      { label: "Report", value: (row: string[]) => row[0] },
      { label: inertText(name), value: (row: string[]) => row[1] },
    ],
  );
}

/** The whole file: preamble, a blank line, then the table. */
export function reportCsvText(input: ReportCsvInput): string {
  const { header, rows } = buildReportCsv(input);
  const csvColumns = header.map((label, i) => ({
    label,
    value: (row: Cell[]) => row[i],
  }));
  return [
    reportCsvPreamble(input.name, input.context),
    "",
    toCSV(rows, csvColumns),
  ].join("\r\n");
}

export function exportReportCsv(input: ReportCsvInput): void {
  downloadCSV(
    reportCsvFileName(input.name, input.dateRange),
    reportCsvText(input),
  );
}

/** Export what the canvas shows: the run's tree in the table's sort order and
 *  the view's visible columns. */
export function exportRunCsv(
  name: string,
  data: RunReportV2Response,
  table: ReportTableModel,
  context: RunContext,
): void {
  exportReportCsv({
    context,
    name,
    dateRange: data.date_range,
    columns: table.columns,
    sortedNodes: table.sortedNodes,
    index: table.index,
    totals: data.totals.values,
    levelLabels: table.levelLabels,
  });
}
